import { Box, ButtonBase, Paper, Typography, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts-pro';
import { Tooltip } from '~/components/Tooltip';
import { InfoTooltip } from '~/components/Tooltip/InfoTooltip';

/** Chart dimension constants */
const CHART_SIZE = 140;
const CHART_CENTER = CHART_SIZE / 2;

/** Donut ring dimensions */
const DONUT_INNER_RADIUS = 40;
const DONUT_OUTER_RADIUS = 65;
const DONUT_PADDING_ANGLE = 2;
const DONUT_CORNER_RADIUS = 4;

/** Legend indicator size */
const LEGEND_DOT_SIZE = 10;

export interface DonutChartData {
  id: string;
  label: string;
  value: number;
  color: string;
  onClick?: () => void;
}

export interface DonutChartProps {
  title: string;
  data: DonutChartData[];
  centerLabel?: string;
  centerValue?: string | number;
  emptyMessage?: string;
  tooltip?: string;
  legendValueFormatter?: (value: number, total: number) => string;
  valueTooltipFormatter?: (value: number) => string;
}

export const DonutChart = ({
  title,
  data,
  centerLabel,
  centerValue,
  emptyMessage,
  tooltip,
  legendValueFormatter,
  valueTooltipFormatter,
}: DonutChartProps) => {
  const theme = useTheme();
  const isEmpty = data.length === 0 || (emptyMessage != null && data.every((d) => d.value === 0));
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        borderLeft: '5px solid',
        borderColor: theme.palette.primary.main,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography component="h2" variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        {tooltip && <InfoTooltip title={tooltip} placement="top" />}
      </Box>
      {isEmpty ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: CHART_SIZE,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {emptyMessage ?? ''}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <PieChart
              series={[
                {
                  data: data.map((item) => ({
                    id: item.id,
                    value: item.value,
                    label: item.label,
                    color: item.color,
                  })),
                  innerRadius: DONUT_INNER_RADIUS,
                  outerRadius: DONUT_OUTER_RADIUS,
                  paddingAngle: DONUT_PADDING_ANGLE,
                  cornerRadius: DONUT_CORNER_RADIUS,
                  cx: CHART_CENTER,
                  cy: CHART_CENTER,
                  ...(valueTooltipFormatter && {
                    valueFormatter: (item: { value: number }) => valueTooltipFormatter(item.value),
                  }),
                },
              ]}
              width={CHART_SIZE}
              height={CHART_SIZE}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              slotProps={{ legend: { hidden: true } }}
              onItemClick={(_, params) => {
                data[params.dataIndex]?.onClick?.();
              }}
              sx={{
                '& .MuiPieArc-root': {
                  cursor: data.some((d) => d.onClick) ? 'pointer' : 'default',
                },
              }}
            />
            {(centerLabel || centerValue !== undefined) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  pointerEvents: 'none',
                }}
              >
                {centerValue !== undefined && (
                  <Typography component="p" variant="h5" fontWeight={700}>
                    {centerValue}
                  </Typography>
                )}
                {centerLabel && (
                  <Typography variant="caption" color="text.secondary">
                    {centerLabel}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {data.map((item) => {
              const legendValue = legendValueFormatter
                ? legendValueFormatter(item.value, total)
                : item.value;
              const row = (
                <Box
                  key={item.id}
                  component={item.onClick ? ButtonBase : 'div'}
                  onClick={item.onClick}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.5,
                    mx: -1,
                    px: 1,
                    borderRadius: 1,
                    ...(item.onClick && {
                      // Unlike a div, a button does not stretch to fill its row.
                      width: `calc(100% + ${theme.spacing(2)})`,
                      textAlign: 'left',
                      '&:hover': { backgroundColor: theme.palette.grey[50] },
                    }),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: LEGEND_DOT_SIZE,
                        height: LEGEND_DOT_SIZE,
                        borderRadius: '50%',
                        backgroundColor: item.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" noWrap>
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={600} sx={{ ml: 1 }}>
                    {legendValue}
                  </Typography>
                </Box>
              );
              if (!valueTooltipFormatter) return row;
              return (
                <Tooltip
                  describeChild
                  key={item.id}
                  title={valueTooltipFormatter(item.value)}
                  placement="left"
                >
                  {row}
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}
    </Paper>
  );
};
